import React, { useState, useEffect } from "react";
import { Link, useNavigate, NavLink } from "react-router-dom";
import { useCart } from "../context/CartContext";
import axios from "axios";
import ReactGA from "react-ga4";
import { useSnackbar } from "notistack";
import { formatarData, formatarValor } from "../components/formatarValores";
import { getDiscountTiers } from "../services/api";
import ModalCapturaCelular from "../components/ModalCapturaCelular";
import { useLanguage } from "../context/LanguageContext";
import { isImpersonating } from "../utils/impersonation";

// import userModel from "../../../backend/models/userModel";

const Cart = () => {
  const { t } = useLanguage();
  const isSimulating = isImpersonating();
  const User = JSON.parse(localStorage.getItem("user"));
  const { cartItems, decreaseCartItemQuantity, addToCart, clearCart } =
    useCart();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [temProdutos, setTemProdutos] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [minPhotos, setMinPhotos] = useState(1);
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  const totalQty = cartItems.reduce((acc, item) => acc + (item.quantity || 0), 0);
  const isCheckoutDisabled = totalQty < minPhotos;

  const subTotalInCents = cartItems.reduce(
    (acc, item) => acc + item.priceInCents * item.quantity,
    0
  );

  const appliedPercent = (() => {
    if (!Array.isArray(tiers) || tiers.length === 0) return 0;
    const applicable = tiers.filter((t) => totalQty >= t.minQty);
    if (applicable.length === 0) return 0;
    return applicable[applicable.length - 1].percent;
  })();

  const discountAmountInCents = Math.round(
    (subTotalInCents * appliedPercent) / 100
  );
  const finalTotalInCents = subTotalInCents - discountAmountInCents;

  useEffect(() => {
    if (localStorage.getItem("adminImpersonation")) return;
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname,
      title: "acessou_carrinho",
    });

    ReactGA.event({
      category: "entrada_no_carrinho",
      action: "atleta_no_carrinho",
      label: User?.nome, // optional
    });
    ReactGA.event({
      category: "entrada_no_carrinho",
      action: "valor_do_carrinho",
      label: subTotalInCents / 100, // optional
    });
  }, []);

  useEffect(() => {
    setTemProdutos(cartItems.length > 0);
  }, [cartItems]);

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const { data } = await getDiscountTiers();
        if (Array.isArray(data)) {
          setTiers(data);
          setMinPhotos(1);
        } else if (data && typeof data === "object") {
          setTiers(Array.isArray(data.tiers) ? data.tiers : []);
          setMinPhotos(data.minPhotos ? Number(data.minPhotos) : 1);
        }
      } catch (e) {
        // Silencia se não houver configuração
        setTiers([]);
        setMinPhotos(1);
      }
    };
    fetchTiers();
  }, []);

  if (cartItems.length === 0) {
    return (
      <div className="text-black text-3xl text-center my-48">
        {t("cart.emptyMessage")}
        <div className=" text-center mb-8">
          <NavLink to="/ar/shop" className="btn btn-accent mt-4">
            {t("cart.goToShop")}
          </NavLink>
        </div>
      </div>
    );
  }

  const gerarChavePIX = async () => {
    const data = {
      codigo: User.id,
      valor: finalTotalInCents / 100,
    };

    try {
      const response = await axios.post(
        `${
          import.meta.env.VITE_REACT_APP_BACKEND_BASEURL
        }/compras/pagamento/gerarPayload`,
        data
      );

      return response.data;
    } catch (error) {
      console.error("Problemas na geração do QRCODE", error);
    }
  };

  // armazenar dados da compra
  const salvarCompra = async () => {
    const chavePix = await gerarChavePIX();
    const data = {
      idAtleta: User.id,
      quantity: totalQty,
      totalPrice: finalTotalInCents / 100,
      subTotalInCents,
      discountPercent: appliedPercent,
      discountAmountInCents,
      status: "Pendente",
      produtos: cartItems,
      chavePix: chavePix?.chavePix,
    };
    try {
      const response = await axios.post(
        `${
          import.meta.env.VITE_REACT_APP_BACKEND_BASEURL
        }/compras/criarCompra`,
        data
      );
      localStorage.setItem("idCompra", response.data.id);

      navigate("/ar/pagamentos/novo");

      return response.data;
    } catch (error) {
      console.error("Problemas na criação da ordem", error);
      enqueueSnackbar(
        "Problemas na criação de sua ordem. Procure o administrador",
        { variant: "warning" }
      );
    }
  };

  const handleCheckout = async () => {
    if (isCheckoutDisabled) {
      enqueueSnackbar(
        t("cart.minPhotosWarning", { min: minPhotos, plural: minPhotos > 1 ? "s" : "" }),
        { variant: "warning" }
      );
      return;
    }

    // Verifica se o atleta logado possui celular cadastrado
    const currentUser = JSON.parse(localStorage.getItem("user")) || User;
    const cleanCelular = currentUser?.celular
      ? String(currentUser.celular).replace(/\D/g, "")
      : "";

    if (!cleanCelular || cleanCelular.length < 8) {
      setShowPhoneModal(true);
      return;
    }

    await salvarCompra();
  };

  return (
    <div className={`p-4 ${isSimulating ? "mt-52" : "mt-44"} max-w-[1400px] mx-auto transition-all`}>
      <table className="table mt-4">
        <thead>
          <tr className="text-base">
            <th className="text-start">{t("cart.photosHeader")}</th>
            <th className="text-center">{t("cart.valueHeader")}</th>
            <th className="text-center">{t("cart.actionHeader")}</th>
          </tr>
        </thead>
        <tbody>
          {cartItems.map((item, index) => (
            <tr key={index} className="bg-base-100 hover:bg-base-300">
              <td className="py-3 px-2 text-center">
                <img
                  src={item.imageSample}
                  alt={item.name}
                  className="rounded-md mb-4 w-[80px] h-[100px] object-cover "
                />
              </td>
              <td className="py-3 px-2 text-center">
                R$
                {(item.priceInCents / 100).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                })}
              </td>

              <td className="py-3 px-2 text-center">
                <button
                  onClick={() => decreaseCartItemQuantity(item._id)}
                  className="  font-bold hover:underline"
                >
                  {t("cart.removeBtn")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={`fixed ${isSimulating ? "top-[118px]" : "top-[74px]"} right-4 z-10 flex flex-col items-end transition-all`}>
        <button
          onClick={handleCheckout}
          disabled={isCheckoutDisabled}
          className={`py-2 px-4 rounded-md text-white font-medium transition-colors ${
            isCheckoutDisabled
              ? "bg-gray-400 cursor-not-allowed opacity-60"
              : "bg-blue-500 hover:bg-blue-800"
          }`}
        >
          {t("cart.proceedToPayment")}
        </button>
        {isCheckoutDisabled && (
          <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1.5 max-w-[220px] text-right  shadow-sm">
            {t("cart.minPhotosWarning", { min: minPhotos, plural: minPhotos === 1 ? "" : "s" })}{" "}
            <Link
              to="/ar/shop"
              className="text-blue-600 hover:text-blue-800 hover:underline font-semibold block mt-1"
            >
              {t("cart.goToShop")}
            </Link>
          </div>
        )}
      </div>
      {temProdutos ? (
        <div className={`fixed ${isSimulating ? "top-[118px]" : "top-[74px]"} left-4 z-4 transition-all`}>
          <div className="bg-base-200 text-black py-2 px-4 rounded-md">
            <div className="flex justify-between gap-4">
              <span>{t("common.subtotal")}:</span>
              <span>
                R$
                {(subTotalInCents / 100).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span>
                {t("common.discount")} {appliedPercent ? `(${appliedPercent}%)` : ""}:
              </span>
              <span>
                - R$
                {(discountAmountInCents / 100).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between gap-4 font-semibold">
              <span>{t("common.total")}:</span>
              <span>
                R$
                {(finalTotalInCents / 100).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      <ModalCapturaCelular
        isOpen={showPhoneModal}
        userId={User?.id}
        onClose={() => setShowPhoneModal(false)}
        onSkip={async () => {
          setShowPhoneModal(false);
          await salvarCompra();
        }}
        onSuccess={async () => {
          setShowPhoneModal(false);
          await salvarCompra();
        }}
      />
    </div>
  );
};
export default Cart;
